import React from 'react';
import { motion } from 'framer-motion';
import {
  MapPin, DollarSign, Truck, X, Eye, Edit3,
  Trash2, Clock, CheckCircle, Building2, ArrowRight, Send, UserCheck,
  Briefcase, Calendar, Phone, Route, Package, Shield
} from 'lucide-react';

const STATUS_COLORS = { open: 'bg-green-100 text-green-700', assigned: 'bg-blue-100 text-blue-700', in_transit: 'bg-yellow-100 text-yellow-700', delivered: 'bg-emerald-100 text-emerald-700', cancelled: 'bg-red-100 text-red-700' };

export const JobCard = ({ job, isDriver, isCarrier, isDispatcher, onView, onApply, onEdit, onDelete }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
    className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-shadow" data-testid={`job-card-${job.id}`}>
    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <h3 className="font-semibold text-slate-900 text-base">{job.title}</h3>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[job.status] || STATUS_COLORS.open}`}>
            {(job.status || 'open').replace('_', ' ')}
          </span>
          {job.route_type && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">{job.route_type}</span>}
          {job.cdl_type && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">{job.cdl_type}</span>}
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
          {job.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{job.location}</span>}
          {(job.pay || job.rate) && <span className="flex items-center gap-1 text-green-600 font-medium"><DollarSign className="h-3.5 w-3.5" />${job.pay || job.rate} {job.pay_type || job.rate_type}</span>}
          {job.equipment_type && <span className="flex items-center gap-1"><Truck className="h-3.5 w-3.5" />{job.equipment_type}</span>}
          {job.schedule && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{job.schedule}</span>}
          {job.freight_type && <span className="flex items-center gap-1"><Package className="h-3.5 w-3.5" />{job.freight_type}</span>}
        </div>
        {job.company && <p className="text-xs text-slate-400 mt-1.5 flex items-center gap-1"><Building2 className="h-3 w-3" />{job.company} {job.mc_number && `| MC: ${job.mc_number}`}</p>}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button onClick={() => onView(job)} className="p-2 text-slate-400 hover:text-blue-500 rounded-lg transition-colors" data-testid={`view-job-${job.id}`}><Eye className="h-4 w-4" /></button>
        {isDriver && (
          job.user_application ? (
            <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-green-50 text-green-700 border border-green-200" data-testid={`applied-badge-${job.id}`}><CheckCircle className="h-3.5 w-3.5" />Applied</span>
          ) : job.status === 'open' ? (
            <button onClick={() => onApply(job)} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium transition-colors" data-testid={`apply-job-${job.id}`}><Send className="h-3.5 w-3.5" />Apply</button>
          ) : null
        )}
        {(isCarrier || isDispatcher) && (
          <>
            {job.application_count > 0 && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700" data-testid={`app-count-${job.id}`}><UserCheck className="h-3 w-3" />{job.application_count}</span>}
            <button onClick={() => onEdit(job)} className="p-2 text-slate-400 hover:text-green-500 rounded-lg transition-colors" data-testid={`edit-job-${job.id}`}><Edit3 className="h-4 w-4" /></button>
            {isCarrier && <button onClick={() => onDelete(job.id)} className="p-2 text-slate-400 hover:text-red-500 rounded-lg transition-colors" data-testid={`delete-job-${job.id}`}><Trash2 className="h-4 w-4" /></button>}
          </>
        )}
      </div>
    </div>
  </motion.div>
);

export const JobViewModal = ({ job, onClose }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm overflow-y-auto py-8" onClick={onClose}>
    <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
      className="bg-white rounded-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 px-6 py-5">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-white mb-1">{job.title}</h3>
            {job.company && <p className="text-slate-300 text-sm flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{job.company}</p>}
          </div>
          <button onClick={onClose} className="p-1.5 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {job.route_type && <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-blue-500/20 text-blue-200">{job.route_type}</span>}
          {job.cdl_type && <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-white/10 text-slate-300">{job.cdl_type}</span>}
          <span className={`px-2.5 py-1 rounded-md text-xs font-medium ${job.status === 'open' ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'}`}>
            {(job.status || 'open').replace('_', ' ')}
          </span>
        </div>
      </div>

      {/* Details */}
      <div className="px-6 py-5 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          {[
            job.location && [MapPin, 'Location', job.location],
            (job.pay || job.rate) && [DollarSign, 'Pay Rate', `$${job.pay || job.rate} (${job.pay_type || job.rate_type})`],
            job.schedule && [Clock, 'Schedule', job.schedule],
            job.equipment_type && [Truck, 'Vehicle Needed', job.equipment_type],
            job.freight_type && [Package, 'Type of Freight', job.freight_type],
            job.experience_required && [Briefcase, 'Experience', job.experience_required],
            job.route_type && [Route, 'Route Type', job.route_type],
            job.start_date && [Calendar, 'Start Date', job.start_date],
            job.mc_number && [Shield, 'MC Number', job.mc_number],
            job.dot_number && [Shield, 'DOT Number', job.dot_number],
          ].filter(Boolean).map(([Icon, label, value]) => (
            <div key={label} className="flex items-start gap-2.5">
              <div className="w-7 h-7 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0 mt-0.5"><Icon className="h-3.5 w-3.5 text-slate-500" /></div>
              <div><p className="text-xs text-slate-400">{label}</p><p className="text-sm font-medium text-slate-900">{value}</p></div>
            </div>
          ))}
        </div>

        {job.contact_info && (
          <div className="flex items-start gap-2.5 pt-2 border-t border-slate-100">
            <div className="w-7 h-7 rounded-md bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5"><Phone className="h-3.5 w-3.5 text-blue-500" /></div>
            <div><p className="text-xs text-slate-400">Contact</p><p className="text-sm font-medium text-slate-900">{job.contact_info}</p></div>
          </div>
        )}

        {job.requirements && (
          <div className="pt-2 border-t border-slate-100">
            <p className="text-xs text-slate-400 mb-2">Requirements</p>
            <div className="flex flex-wrap gap-1.5">
              {job.requirements.split(', ').filter(Boolean).map(r => (
                <span key={r} className="px-2 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-md text-xs font-medium">{r}</span>
              ))}
            </div>
          </div>
        )}

        {job.description && (
          <div className="pt-2 border-t border-slate-100">
            <p className="text-xs text-slate-400 mb-1">Job Description</p>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{job.description}</p>
          </div>
        )}

        <div className="pt-3 border-t border-slate-100 text-xs text-slate-400">
          Posted by {job.posted_by_name || job.company || 'Unknown'} on {job.posted_at ? new Date(job.posted_at).toLocaleDateString() : '-'}
        </div>
      </div>
    </motion.div>
  </motion.div>
);

export const JobApplyModal = ({ job, message, setMessage, onSubmit, submitting, onClose }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
    <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
      className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="apply-modal">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-slate-900">Apply for Job</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
      </div>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
        <p className="font-medium text-blue-900">{job.title}</p>
        <div className="flex items-center gap-3 text-sm text-blue-700 mt-1">
          {job.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{job.location}</span>}
          {job.company && <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{job.company}</span>}
        </div>
        {(job.pay || job.rate) && <p className="text-sm text-green-700 font-medium mt-1">${job.pay || job.rate} ({job.pay_type || job.rate_type})</p>}
      </div>
      <div className="mb-4">
        <label className="text-sm font-medium text-slate-700 mb-1 block">Message (optional)</label>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
          placeholder="Tell the carrier about your experience and why you're a good fit..."
          className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="apply-message-input" />
      </div>
      <button onClick={onSubmit} disabled={submitting}
        className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        data-testid="submit-apply-btn">
        {submitting ? <><Clock className="h-4 w-4 animate-spin" />Submitting...</> : <><Send className="h-4 w-4" />Submit Application</>}
      </button>
    </motion.div>
  </motion.div>
);
